/**
 * Functions related to the pricing plan that the user is on, this information is
 * mostly captured in a "planId" string that we store with each purchase on Stripe
 * and this module helps parse that string and determine what plan the user is on.
 */

export type Plan = {
  planId: string | null;
  type: 'free' | 'professional' | 'clinic' | 'enterprise' | 'unknown';
  version?: 'v1' | 'v2';
  interval?: 'once' | 'daily' | 'monthly' | 'yearly' | 'unknown';
  currency?: string;
};

export const FREE_PLAN: Plan = {
  planId: null,
  type: 'free',
};

function translatePlanType(
  type: string,
): 'free' | 'professional' | 'clinic' | 'enterprise' | 'unknown' {
  switch (type) {
    case 'free': {
      return 'free';
    }
    // Note, "professional" used to be called "premium"
    case 'premium': {
      return 'professional';
    }
    case 'pro': {
      return 'professional';
    }
    case 'professional': {
      return 'professional';
    }
    case 'clinic': {
      return 'clinic';
    }
    case 'enterprise': {
      return 'enterprise';
    }
    default: {
      return 'unknown';
    }
  }
}

function translateInterval(
  interval: string,
): 'once' | 'daily' | 'monthly' | 'yearly' | 'unknown' {
  switch (interval) {
    case 'one': {
      return 'once';
    }
    case 'daily': {
      return 'daily';
    }
    case 'monthly': {
      return 'monthly';
    }
    case 'yearly': {
      return 'yearly';
    }
    default: {
      return 'unknown';
    }
  }
}

/**
 * Given the plan id string i.e. premium-monthly-v1, plan-default-usd-pro_monthly-v2, premium-yearly-v1
 * parse the plan so that we can have the details
 */
export function parsePlanString(planId: string): Plan {
  if (!planId) {
    return FREE_PLAN;
  }

  if (planId.endsWith('v1')) {
    const parts = planId.split('-');

    let planType = null;
    let interval = null;
    let version = null;

    switch (parts.length) {
      case 4:
        planType = parts[0];
        interval = parts[1];
        version = parts[3];
        break;
      default:
        [planType, interval, version] = parts;
    }

    return {
      planId,
      type: translatePlanType(String(planType)),
      version: version as 'v1' | 'v2',
      interval: translateInterval(String(interval)),
    };
  }

  if (planId.endsWith('v2')) {
    const [_, __, currency, planTypeInterval, version] = planId.split('-');
    const [type, interval] = String(planTypeInterval).split('_');

    return {
      planId,
      type: translatePlanType(String(type)),
      version: version as 'v1' | 'v2',
      interval: translateInterval(String(interval)),
      currency,
    };
  }

  if (
    planId.startsWith('price') || // custom price item in Stripe
    planId.startsWith('plan_') || // custom price item in Stripe
    planId === '50% off old user' // a somewhat rare human-readable price tier {
  ) {
    return {
      planId,
      type: 'unknown',
    };
  }

  return FREE_PLAN;
}

/**
 * Given a planId and a seatCount return what plan the user is on
 * @param planId string A string like "plan-default-usd-pro_monthly-v2" which is used to capture what sort of plan the user is on
 * @param seatCount number The number of seat licenses the user has purchased
 */
export function determinePlan(planId?: string, seatCount?: number): Plan {
  if (Boolean(planId)) {
    const plan = parsePlanString(String(planId));

    // Often there will be plans like price_<identifier> which default
    // to "unknown" however, the vast majority of time these are clinic
    // accounts, and thus enterprise accounts if the seat count is over 50
    // see https://doxyme.slack.com/archives/C016RUTSMHR/p1733747180068829
    if (Boolean(seatCount) && Number(seatCount) >= 50) {
      plan.type = 'enterprise';
      return plan;
    }

    if (plan.type === 'unknown') {
      plan.type = 'clinic';
      return plan;
    }

    return plan;
  }
  return FREE_PLAN;
}
